// frontend/src/utils/config.ts
// ✨ Centralized API Configuration - Single Source of Truth
// 
// This file consolidates all backend URL definitions across the app.
// Previously scattered across 27+ files with inconsistent defaults.
//
// ARCHITECTURE:
// - Frontend: React dev server on port 3000
// - Backend: Express API on port 5000 (or 3000 in some envs)
// - Production: https://api.XPROJEX.com

/**
 * Detect environment
 */
import logger from './logger';

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';
const CANONICAL_API_ORIGIN = 'https://api.xprojex.com';
const LOCAL_API_ORIGIN = 'http://localhost:5000';

const isLoopbackHostname = (hostname: string): boolean => (
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
);

export const resolveTrustedBackendUrl = (
  value: string | undefined,
  environment = process.env.NODE_ENV,
): string => {
  const fallback = environment === 'development' ? LOCAL_API_ORIGIN : CANONICAL_API_ORIGIN;
  if (!value?.trim()) return fallback;

  try {
    const url = new URL(value.trim());
    const hasUnexpectedParts = Boolean(
      url.username
      || url.password
      || url.search
      || url.hash
      || (url.pathname !== '/' && url.pathname !== '')
    );
    if (hasUnexpectedParts) return fallback;

    if (environment === 'development') {
      return ['http:', 'https:'].includes(url.protocol) && isLoopbackHostname(url.hostname)
        ? url.origin
        : fallback;
    }

    return url.origin.toLowerCase() === CANONICAL_API_ORIGIN
      ? CANONICAL_API_ORIGIN
      : fallback;
  } catch {
    return fallback;
  }
};

export const resolveTrustedWebSocketUrl = (
  value: string | undefined,
  environment = process.env.NODE_ENV,
): string => {
  const fallback = environment === 'development'
    ? 'ws://localhost:5000/ws'
    : 'wss://api.xprojex.com/ws';
  if (!value?.trim()) return fallback;

  try {
    const url = new URL(value.trim());
    const hasUnexpectedParts = Boolean(url.username || url.password || url.search || url.hash);
    if (hasUnexpectedParts || !['/', '/ws'].includes(url.pathname)) return fallback;

    if (environment === 'development') {
      return ['ws:', 'wss:'].includes(url.protocol) && isLoopbackHostname(url.hostname)
        ? url.toString().replace(/\/$/, '')
        : fallback;
    }

    return url.protocol === 'wss:'
      && url.hostname.toLowerCase() === 'api.xprojex.com'
      && (url.port === '' || url.port === '443')
      ? url.toString().replace(/\/$/, '')
      : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Default backend URL based on environment
 * Dev: Assumes backend running on localhost:5000
 * Prod: Falls back to production API if env var missing
 */
const DEFAULT_BACKEND_URL = resolveTrustedBackendUrl(undefined);
const CONFIGURED_BACKEND_URL = resolveTrustedBackendUrl(process.env.REACT_APP_BACKEND_URL);
const CONFIGURED_WEBSOCKET_URL = resolveTrustedWebSocketUrl(process.env.REACT_APP_WEBSOCKET_URL);

/**
 * Main API Configuration
 * 
 * Environment variables take precedence over defaults.
 * This ensures production deploys work correctly while
 * providing sensible dev defaults.
 */
export const API_CONFIG = {
  /**
   * Main backend API URL
   * Env var: REACT_APP_BACKEND_URL
   * Dev default: http://localhost:5000
   * Prod default: https://api.XPROJEX.com
   */
  BACKEND_URL: CONFIGURED_BACKEND_URL,

  /**
   * WebSocket URL for real-time features
   * Env var: REACT_APP_WEBSOCKET_URL
   */
  WEBSOCKET_URL: CONFIGURED_WEBSOCKET_URL,

  /**
   * Wix integration URLs (for embedded app)
   */
  WIX_BACKEND_URL: process.env.REACT_APP_WIX_BACKEND_URL || 'https://www.XPROJEX.com',
  WIX_CLIENT_ID: process.env.REACT_APP_WIX_CLIENT_ID || '',

  /**
   * Helper to build full endpoint URL
   * @param path - API path (e.g., '/api/opportunities')
   * @returns Full URL
   * 
   * @example
   * API_CONFIG.endpoint('/api/opportunities') 
   * // => 'http://localhost:5000/api/opportunities'
   */
  endpoint(path: string): string {
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.BACKEND_URL}${normalizedPath}`;
  },

  /**
   * Check if we're in development mode
   */
  get isDevelopment(): boolean {
    return isDevelopment;
  },

  /**
   * Check if we're in production mode
   */
  get isProduction(): boolean {
    return isProduction;
  },
};

/**
 * Validation & logging on app startup
 * Helps developers catch configuration issues early
 */
if (isDevelopment) {
  logger.info('🔧 API Configuration:', {
    BACKEND_URL: API_CONFIG.BACKEND_URL,
    WEBSOCKET_URL: API_CONFIG.WEBSOCKET_URL,
    usingEnvVar: !!process.env.REACT_APP_BACKEND_URL,
    environment: process.env.NODE_ENV,
  });

  // Warn if using default (no .env file)
  if (!process.env.REACT_APP_BACKEND_URL) {
    logger.warn(
      '⚠️ REACT_APP_BACKEND_URL not set. Using default:',
      DEFAULT_BACKEND_URL,
      '\nCreate a .env file with REACT_APP_BACKEND_URL=http://localhost:5000'
    );
  }
}

// Production validation
if (isProduction && !process.env.REACT_APP_BACKEND_URL) {
  logger.error(
    '❌ PRODUCTION ERROR: REACT_APP_BACKEND_URL not configured!',
    'Falling back to:', DEFAULT_BACKEND_URL
  );
}

/**
 * Legacy support - export individual constants for gradual migration
 * @deprecated Use API_CONFIG instead
 */
export const BACKEND_URL = API_CONFIG.BACKEND_URL;
export const WEBSOCKET_URL = API_CONFIG.WEBSOCKET_URL;

export default API_CONFIG;
